<template>
  <nav
    v-if="hasContent"
    ref="panel"
    class="panel edge-to-edge"
    :class="{ 'is-opened': isOpen }"
    @transitionend="onTransitionEnd"
  >
    <a class="panel-heading is-size-6" @click.prevent="toggle">
      <span>
        <span v-if="iconClasses" class="icon mr-2">
          <i :class="iconClasses" />
        </span>
        <slot name="label">{{ label }}</slot>
      </span>
      <span class="header-right">
        <slot name="header-right" />
        <span class="icon is-size-7 toggle-icon">
          <i
            class="fa-solid"
            :class="isOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
          />
        </span>
      </span>
    </a>
    <section ref="block" class="panel-block">
      <slot />
    </section>
  </nav>
</template>

<script>
export default {
  name: 'ToggleSection',
  props: {
    label: { type: String, default: '' },
    iconClasses: { type: String, default: '' },
    collapsed: { type: Boolean, default: true },
    hasContent: { type: Boolean, default: true },
  },
  emits: ['toggle'],
  data() {
    return { isOpen: !this.collapsed }
  },
  watch: {
    collapsed(val) {
      this.isOpen = !val
      if (val) this.clipBlock()
    },
  },
  mounted() {
    if (this.isOpen) this.unclipBlock()
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen
      this.$emit('toggle', this.isOpen)
      if (!this.isOpen) this.clipBlock()
    },
    onTransitionEnd(e) {
      if (e.target !== this.$refs.panel) return
      if (this.isOpen) this.unclipBlock()
    },
    clipBlock() {
      if (this.$refs.block) this.$refs.block.style.overflow = 'hidden'
    },
    unclipBlock() {
      if (this.$refs.block) this.$refs.block.style.overflow = 'visible'
    },
  },
}
</script>

<style lang="scss" scoped>
nav.panel {
  padding: 0;
  box-shadow: none;
  display: grid;
  grid-template-rows: min-content 0fr;
  transition: grid-template-rows 0.25s ease-out;

  &.is-opened {
    grid-template-rows: min-content 1fr;
  }

  .panel-heading {
    display: flex;
    padding: var(--space-2) var(--panel-padding-x);
    border-radius: 2px;
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--text-default);
    background-color: var(--bg-muted);
    border-bottom: 1px solid var(--border-default);
  }

  // When open, round only top corners and highlight
  &.is-opened > .panel-heading {
    border-radius: 2px 2px 0 0;
    background-color: var(--bg-subtle);
  }

  a.panel-heading {
    &:hover {
      background-color: var(--bg-subtle);
    }
    & > *:first-child {
      flex-grow: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .panel-block {
    padding: 0;
    overflow: hidden;
    min-height: 0;
    display: block;
  }

  &.is-opened > .panel-block {
    padding-bottom: var(--space-3);
  }
}
</style>
